import type { FacilityDocumentPickerItem } from "components/FacilityDocumentPicker";

import { useLazyQuery, useQuery } from "@apollo/client";
import Download from "@mui/icons-material/Download";
import {
  Breakpoint,
  Button,
  Checkbox,
  FormControlLabel,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { CA_DocumentOption } from "encamp-compliance-engine/src/types/facilityStateFieldTypes";
import {
  isSystemManagedDocumentType,
  KnownSubmissionDocumentTypes,
} from "encamp-shared/src/documents";
import { prettyPrintDocumentType } from "encamp-shared/src/utils/prettyPrintDocumentType";
import { CaDocumentType, DocumentAssociationsInput, DocumentType } from "generated-graphql/graphql";
import { useCurrentUser } from "hooks/useCurrentUser";
import { useDocumentDownload } from "hooks/useDocumentDownload";
import { useDocumentUpload } from "hooks/useDocumentUpload";
import { useFilenameStandardization } from "hooks/useFilenameStandardization";
import { useUpsertDocument } from "hooks/useUpsertDocument";
import { useValidatingForm } from "hooks/useValidatingForm";
import GET_DOCUMENT_DOWNLOAD_LINK_QUERY from "queries/getDocumentDownloadLink";
import { useCallback, useEffect, useMemo } from "react";
import { FormProvider, SubmitHandler } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import {
  isHmbpDocumentType,
  useHmbpDocumentOptions,
  useHmbpFeature,
  useHmbpStateFieldMutation,
} from "routes/Customer/Chemicals/Report/useHmbp";
import { useDocumentIssuesForModel } from "routes/Customer/Chemicals/Report/useReport";
import { useDocumentInputValidation } from "routes/Customer/Chemicals/Report/validationHooks/useDocumentInputValidation";
import { documentTypeToLabel } from "util/constants";
import { hasCriticalIssues } from "util/forms";

import { type DocumentFilePickerProps, isPreviewable, useDocumentFiles } from "../shared";
import { FormStep } from "./components/FormStep";
import { UploadStep } from "./components/UploadStep";
import { useAcceptableFileFormats } from "./hooks/useAcceptableFileFormats";
import { useDialogState } from "./hooks/useDialogState";
import { GET_UPSERTED_DOCUMENT } from "./queries";
import {
  AddEditDocumentDialogProps,
  DocumentFormData,
  EditMode,
  FormState,
  defaultAddEditDocumentDialogState,
} from "./types";
import { getAssociations } from "./utils/getAssociations";

export function AddEditDocumentDialog({
  onSubmit,
  dialogState,
  setDialogState,
  documentTypeOptions,
  activityPickerOptions,
  tenantId,
  reportId,
  reportingYear,
  state,
  showFacilityPicker,
  showTagPicker,
  facilityDocumentIssues,
  showAttestation,
  generateTitleOnCreate,
  refetchQueries,
  isReadOnly = false,
  dialogHeight = 960,
  disableDocumentTypeSelection = false,
  existingDefaults,
}: AddEditDocumentDialogProps) {
  const theme = useTheme();
  const alerts = useAlerts();
  const { user } = useCurrentUser();
  const { fetchAndHandleDownload, clearDownloadLink } = useDocumentDownload();
  const navigate = useNavigate();
  const hmbpFeature = useHmbpFeature(state ?? "");

  const { setDocumentOptionToUpload, loading: loadingStateFields } = useHmbpStateFieldMutation();
  const {
    pickedDocument,
    setPickedDocument,
    shouldFieldsBeDisabled,
    attestation,
    setAttestation,
    hasInvalidFileExtension,
    setHasInvalidFileExtension,
    handleDocumentPick,
  } = useDialogState({ dialogState, setDialogState });

  const initialIssues = useDocumentIssuesForModel(dialogState.documentId ?? "");
  const defaultValues: DocumentFormData = useMemo(
    () => ({
      title: dialogState.title ?? "",
      documentType: dialogState.documentType,
      activityId: dialogState.activityId,
      fileExtension: dialogState.fileExtension,
      storageLink: dialogState.storageLink,
      currentSubmissionDocument: dialogState.currentSubmissionDocument,
      facilities: dialogState.facilities || [],
      documentTags: dialogState.documentTags ?? [],
      authoredAt: dialogState.authoredAt,
      description: dialogState.description,
    }),
    [
      dialogState.title,
      dialogState.documentType,
      dialogState.activityId,
      dialogState.fileExtension,
      dialogState.storageLink,
      dialogState.currentSubmissionDocument,
      dialogState.facilities,
      dialogState.documentTags,
      dialogState.authoredAt,
      dialogState.description,
    ]
  );

  const { data, loading: isLoadingDownloadLink } = useQuery(GET_DOCUMENT_DOWNLOAD_LINK_QUERY, {
    variables: { id: dialogState.documentId ?? "" },
    skip: !dialogState.documentId,
  });

  const { uploadFile, isUploading } = useDocumentUpload({
    generateTitleOnCreate: generateTitleOnCreate,
    onSuccess: (document) => {
      onClose();
      if (onSubmit) {
        onSubmit(document, dialogState);
      }
      reset();
      setAttestation(false);
      alerts.success("Successfully uploaded the file");
    },
    onError: (error) => {
      alerts.error("An error occurred while uploading the document.", error);
    },
    refetchQueries,
  });

  const { upsertDocument } = useUpsertDocument();
  const [fetchUpsertedDocument] = useLazyQuery(GET_UPSERTED_DOCUMENT);

  const form = useValidatingForm<DocumentFormData>(
    defaultValues,
    initialIssues,
    useDocumentInputValidation(),
    [],
    { isDisplayOnly: isReadOnly }
  );

  const { handleSubmit, reset, control, setValue, watch, issues, trigger, setError, clearErrors } =
    form;

  const watchedActivityId = watch("activityId");
  const watchTitle = watch("title");
  const watchDocumentType = watch("documentType");
  const watchFileExtension = watch("fileExtension");

  const documentOption = useHmbpDocumentOptions(watchDocumentType);
  const showHmbpWarning =
    hmbpFeature &&
    (documentOption === CA_DocumentOption[CA_DocumentOption["Provided Elsewhere"]] ||
      documentOption === CA_DocumentOption[CA_DocumentOption["Exempt"]]);

  const isInvalidSds =
    watchDocumentType === DocumentType.SafetyDataSheet &&
    watchFileExtension?.toLowerCase() !== "pdf";

  const uncheckedRequiredAttestation = showAttestation && !attestation;

  const saveDisabled = useMemo(() => {
    return (
      hasCriticalIssues(issues) ||
      isInvalidSds ||
      uncheckedRequiredAttestation ||
      (!form.formState.isDirty && !pickedDocument)
    );
  }, [issues, isInvalidSds, uncheckedRequiredAttestation, form.formState.isDirty, pickedDocument]);

  useEffect(() => {
    if (watchTitle && (!watchFileExtension || watchFileExtension === "")) {
      const match = /\.[0-9a-z]+$/i.exec(watchTitle);
      if (match) {
        setValue("fileExtension", match[0].slice(1)); // Remove the leading "."
      }
    }
  }, [watchTitle, setValue, watchFileExtension]);

  const activitySelectItems = useMemo(() => {
    if (activityPickerOptions === undefined) {
      return undefined;
    }
    const options = activityPickerOptions.map((v) => ({
      display: v.title,
      value: v.id as string | null,
    }));
    if (watchedActivityId) {
      options.unshift({ display: "None", value: null });
    }
    return options;
  }, [watchedActivityId, activityPickerOptions]);

  useEffect(() => {
    let msg = "";
    const titleAndExtensionIssues = issues?.filter(
      (issue) => issue.key === "title" || issue.key === "fileExtension"
    );

    if (!titleAndExtensionIssues?.length) {
      clearErrors("title");
      return;
    }

    for (const issue of titleAndExtensionIssues ?? []) {
      if (msg === "") {
        msg += issue.message;
      } else {
        msg += "|" + issue.message;
      }
    }

    setError("title", { message: msg });
  }, [issues, setError, clearErrors]);

  // dialogState.selectedFile is set when we are adding a document
  // data?.getDocumentDownloadLink is set when we are editing a document
  // There are both b/c one is a File and the other is a string link, and it's not worth the
  // pain to unify them
  const hasSelectedFile = useMemo(() => {
    return !!(dialogState.selectedFile || data?.getDocumentDownloadLink);
  }, [dialogState.selectedFile, data?.getDocumentDownloadLink]);

  // Memoize the URL so that the document preview will not unnecessarily reload which causes it to flash
  const fileUrl = useMemo(() => {
    return dialogState.selectedFile
      ? URL.createObjectURL(dialogState.selectedFile ?? new Blob())
      : (data?.getDocumentDownloadLink ?? "");
  }, [dialogState.selectedFile, data?.getDocumentDownloadLink]);

  const facilityName = useMemo(() => {
    const facilities = dialogState.facilities;
    if (facilities?.length === 1) {
      return facilities[0].name || "";
    }
    return "";
  }, [dialogState.facilities]);

  // Use filename standardization hook
  const filenameStandardization = useFilenameStandardization({
    originalFilename: dialogState.selectedFile?.name,
    documentType: watchDocumentType ?? DocumentType.Other,
    facilityName,
    date: new Date(),
    isEdit: dialogState.mode === EditMode.EDIT,
    tenantId,
    reportingYear,
  });

  useEffect(() => {
    if (dialogState.selectedFile) {
      setValue("title", filenameStandardization.standardizedFilename, {
        shouldDirty: true,
      });
    }
  }, [dialogState.selectedFile, filenameStandardization.standardizedFilename, setValue]);

  const removeDocumentIdFromUrl = () => {
    const pathSegments = location.pathname.split("/").filter(Boolean);
    // Only do this if we are on the documents list page and there is a document id in the url
    if (pathSegments.length === 4 && pathSegments[2] === "documents") {
      pathSegments.pop(); // Remove the last segment

      const newPath = `/${pathSegments.join("/")}${location.search}`;
      navigate(newPath);
    }
  };

  const onClose = () => {
    setDialogState(defaultAddEditDocumentDialogState);
    reset();
    setAttestation(false);
    setHasInvalidFileExtension(false);
    resetUploadFiles();
    removeDocumentIdFromUrl();
  };

  const caDocumentTypes = Object.values(CaDocumentType) as unknown as DocumentType[];
  const defaultDocumentTypeOptions = Object.values(DocumentType).filter(
    (x) => !caDocumentTypes.includes(x) && !isSystemManagedDocumentType(x)
  );

  const selectedDocumentType = dialogState.documentType;

  // A system-managed type is owned by ingest and can't be reassigned (the upsert
  // resolver rejects any change to or from it). When editing such a document, lock the
  // picker to that single type instead of offering normal types that would fail on save.
  const uniqueDocumentTypes =
    selectedDocumentType && isSystemManagedDocumentType(selectedDocumentType)
      ? [selectedDocumentType]
      : // First deduplicate the document types, then map to options
        [
          ...new Set([
            ...(documentTypeOptions ?? defaultDocumentTypeOptions),
            // include the already selected document type in case it's not in the list
            ...(selectedDocumentType ? [selectedDocumentType] : []),
          ]),
        ];

  const documentPickerOptions = uniqueDocumentTypes
    .map((v) => ({
      label: documentTypeToLabel(v),
      value: v,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const handleFileSelect = async (files: File[]) => {
    const file = files?.[0];
    if (!file) {
      return;
    }

    const match = /\.[0-9a-z]+$/i.exec(file.name);
    const fileExtension = match ? match[0].slice(1) : "";

    // Check if the file extension is valid for the selected document type
    if (dialogState.documentType && acceptableFileFormats.length > 0) {
      const isValidExtension = acceptableFileFormats.includes(fileExtension.toLowerCase());
      setHasInvalidFileExtension(!isValidExtension);
      if (!isValidExtension) {
        return;
      }
    } else {
      setHasInvalidFileExtension(false);
    }

    // Set form values with shouldDirty: true to mark form as dirty
    // This is needed because programmatic setValue calls don't mark form as dirty by default,
    // but we need the form to recognize these changes for validation and submission logic
    setValue("fileExtension", fileExtension, { shouldDirty: true });
    setValue("fileSize", file.size, { shouldDirty: true });

    setDialogState((state) => ({
      ...state,
      selectedFile: file,
      step: FormState.FORM,
    }));

    await trigger(["title", "fileExtension", "documentType"]);
  };

  const {
    files: uploadFiles,
    handleDrop: handleUploadDrop,
    removeFile: removeUploadFile,
    canAddMore: canAddMoreUploads,
    resetFiles: resetUploadFiles,
  } = useDocumentFiles({
    multiple: false,
    onFilesAdded: (newFiles) => {
      void handleFileSelect(newFiles);
    },
    onFilesCleared: () => {
      setDialogState((state) => ({
        ...state,
        selectedFile: undefined,
        fileExtension: undefined,
      }));
    },
  });

  const handleFacilityDocumentPick = useCallback(
    (document: FacilityDocumentPickerItem) => {
      resetUploadFiles();
      handleDocumentPick(document);
    },
    [handleDocumentPick, resetUploadFiles]
  );

  const resetToUploadStep = () => {
    setPickedDocument(null);
    setDialogState((state) => ({
      ...state,
      step: FormState.UPLOAD,
      selectedFile: undefined,
      fileExtension: undefined,
    }));
    resetUploadFiles();
  };

  const facilityId =
    dialogState.facilities?.length === 1 ? dialogState.facilities[0].id : undefined;

  const acceptableFileFormats = useAcceptableFileFormats({
    tenantId,
    facilityId,
    documentType: dialogState.documentType,
  });

  // Wait until we have a user before rendering
  if (!user) {
    return null;
  }

  const onAddSubmit: SubmitHandler<DocumentFormData> = async (data) => {
    // Since the File type is immutable, create a new file
    // with the name from the form submission
    const file = dialogState.selectedFile
      ? new File([dialogState.selectedFile], data.title ?? dialogState.selectedFile.name, {
          type: dialogState.selectedFile.type,
        })
      : undefined;

    const associations: DocumentAssociationsInput[] = getAssociations(
      data,
      tenantId,
      reportId,
      facilityId
    );

    const documentData = {
      id: data.id,
      tenantId: tenantId ?? "",
      documentType: data.documentType ?? DocumentType.Other,
      title: data.title ?? file?.name,
      description: data.description,
      documentTags: data.documentTags,
      authoredAt: data.authoredAt,
      storageLink: data.storageLink ?? undefined,
      fileExtension: data.fileExtension?.replace(".", "") ?? undefined,
    };

    const isKnownSubmissionType = KnownSubmissionDocumentTypes.includes(
      data.documentType as DocumentType
    );
    const hasMatchingIssue = facilityDocumentIssues?.some(
      (i) => i.key === "facilityDocuments" && i.metadata?.documentType === data.documentType
    );
    const shouldSetCurrentSubmission =
      isKnownSubmissionType && (hasMatchingIssue || dialogState.currentSubmissionDocument);

    await uploadFile({
      file,
      documentData,
      associations,
      currentSubmissionDocument: shouldSetCurrentSubmission ?? false,
    });
  };

  const onEditSubmit: SubmitHandler<DocumentFormData> = async (data) => {
    try {
      const associations: DocumentAssociationsInput[] = getAssociations(
        data,
        tenantId,
        reportId,
        facilityId
      );

      if (dialogState?.selectedFile) {
        // Since the File type is immutable, create a new file
        // with the name from the form submission
        const file = new File(
          [dialogState.selectedFile],
          data.title ?? dialogState.selectedFile.name,
          { type: dialogState.selectedFile.type }
        );

        const isKnownSubmissionType = KnownSubmissionDocumentTypes.includes(
          data.documentType as DocumentType
        );
        const shouldSetCurrentSubmission =
          isKnownSubmissionType &&
          (dialogState.currentSubmissionDocument || data.currentSubmissionDocument);

        await uploadFile({
          file,
          documentData: {
            id: dialogState.documentId,
            tenantId: tenantId ?? "",
            documentType: data.documentType ?? DocumentType.Other,
            title: file.name,
            description: data.description,
            documentTags: data.documentTags,
            authoredAt: data.authoredAt,
            fileExtension: data.fileExtension?.replace(".", "") ?? undefined,
          },
          associations,
          currentSubmissionDocument: shouldSetCurrentSubmission ?? false,
        });
      } else {
        setDialogState((state) => ({ ...state, saving: true }));
        const associationsToRemove = defaultValues.facilities?.filter(
          (existingFacility) =>
            !data.facilities?.some((newFacility) => existingFacility.id === newFacility.id)
        );
        const isKnownSubmissionType = KnownSubmissionDocumentTypes.includes(
          data.documentType as DocumentType
        );
        const shouldSetCurrentSubmission =
          isKnownSubmissionType &&
          (dialogState.currentSubmissionDocument || data.currentSubmissionDocument);

        const documentId = await upsertDocument({
          documentData: {
            id: dialogState.documentId,
            tenantId: tenantId ?? "",
            documentType: data.documentType,
            title: data.title,
            description: data.description,
            documentTags: data.documentTags,
            fileExtension: data.fileExtension,
            storageLink: data.storageLink,
            authoredAt: data.authoredAt,
          },
          generateTitleOnCreate: generateTitleOnCreate,
          associations,
          removeAssociations:
            associationsToRemove?.map((f) => ({
              facilityId: f.id ?? "",
            })) ?? [],
          currentSubmissionDocument: shouldSetCurrentSubmission ?? false,
        });
        const { data: upsertedDocumentData } = await fetchUpsertedDocument({
          variables: { id: documentId ?? "" },
        });
        const document = upsertedDocumentData?.document;

        if (hmbpFeature && data.documentType && isHmbpDocumentType(data.documentType)) {
          await setDocumentOptionToUpload(data.documentType);
        }

        onClose();
        if (onSubmit) {
          onSubmit(
            document ?? {
              id: dialogState.documentId ?? "",
              title: data.title ?? dialogState?.selectedFile?.name ?? "",
              documentType: data.documentType ?? DocumentType.Other,
              fileExtension: data.fileExtension ?? "",
            },
            dialogState
          );
        }
        reset();
        setAttestation(false);
        alerts.success("File successfully edited");
      }
    } catch (err) {
      console.error(`${err}`);
      alerts.error("An error occurred while editing the document");
      setDialogState((state) => ({ ...state, saving: false }));
    }
  };

  const handleDocumentTypeChange = (documentType: DocumentType) => {
    setDialogState((state) => ({
      ...state,
      documentType,
    }));
  };

  const showFacilityDocumentPicker =
    dialogState.mode === EditMode.ADD_OR_PICK && Boolean(facilityId);

  const shouldShowAttestation = hasSelectedFile && !isReadOnly && showAttestation;

  const documentTypeLabel = dialogState.documentType
    ? documentTypeToLabel(dialogState.documentType)
    : "";

  let title = `${documentTypeLabel} Document`;
  if (!isReadOnly && dialogState.mode) {
    title = `${dialogState.mode === EditMode.ADD_OR_PICK ? "Add" : dialogState.mode} ${title}`;
  }

  const currentFileExtension = watchFileExtension ?? dialogState?.fileExtension ?? undefined;

  const displayPreview =
    (hasSelectedFile || isLoadingDownloadLink) && isPreviewable(currentFileExtension);

  const hmbpWarningMessage = showHmbpWarning
    ? `${
        prettyPrintDocumentType(dialogState.documentType) ?? "This document type"
      } was previously designated as "${documentOption}" and will be changed to the "Upload Document" option.`
    : undefined;

  let dialogWidth: Breakpoint = "xs";
  if (displayPreview) {
    dialogWidth = "lg";
  } else if (hasSelectedFile || isLoadingDownloadLink) {
    dialogWidth = "md";
  }

  const dialogCommonProps = {
    open: dialogState.open,
    onClose,
    fullWidth: true,
    title,
    noPrimaryAction: isReadOnly,
    primaryActionButtonProps: {
      onClick: handleSubmit(
        // TODO: We can consolidate these two handlers into one. The logic should be the same now that we are using the upsertDocument hook which handles adding, editing and file upload.
        dialogState.mode === "Add" ? onAddSubmit : onEditSubmit
      ),
      disabled: saveDisabled || hasInvalidFileExtension,
      loading: isUploading || dialogState.saving || loadingStateFields,
    },
    additionalPrimaryActionElements: isReadOnly
      ? [
          <Button key="done-button" onClick={onClose}>
            Done
          </Button>,
        ]
      : undefined,
    secondaryActionElements: [
      <Stack
        key="secondary-action-elements"
        direction="row"
        alignItems="center"
        gap={theme.spacing(1)}
      >
        {dialogState.documentId && (
          <Button
            endIcon={<Download />}
            onClick={async () => {
              await fetchAndHandleDownload({
                documentId: dialogState.documentId ?? "",
              });
              clearDownloadLink();
            }}
          >
            Download
          </Button>
        )}
        {shouldShowAttestation && (
          <Stack direction="row" alignItems="center">
            <FormControlLabel
              control={
                <Checkbox
                  checked={attestation}
                  onChange={(event) => setAttestation(event.target.checked)}
                />
              }
              label={
                <Typography variant="body2">
                  I reviewed and can confirm that all information and documents provided are correct
                </Typography>
              }
            />
          </Stack>
        )}
      </Stack>,
    ],
  };

  const uploadStepFilePickerProps: DocumentFilePickerProps = {
    files: uploadFiles,
    onDrop: handleUploadDrop,
    onRemove: removeUploadFile,
    canAddMore: canAddMoreUploads,
    multiple: false,
    maxFiles: 1,
    disabled: false,
    showFileList: false,
    showPreviewButton: false,
    dropzoneHeight: theme.spacing(29),
    dropzoneMinWidth: theme.spacing(48),
    helperText: "Drag and drop your document or click to select file",
  };

  return (
    <FormProvider {...form}>
      {dialogState.step === FormState.UPLOAD ? (
        <UploadStep
          dialogProps={dialogCommonProps}
          dialogWidth={dialogWidth}
          showFacilityDocumentPicker={showFacilityDocumentPicker}
          facilityId={facilityId}
          documentPickerOptions={documentPickerOptions}
          selectedDocumentType={dialogState.documentType}
          onDocumentTypeChange={handleDocumentTypeChange}
          onDocumentPick={handleFacilityDocumentPick}
          documentFilePickerProps={uploadStepFilePickerProps}
          acceptableFileFormats={acceptableFileFormats}
          hasInvalidFileExtension={hasInvalidFileExtension}
          isUploading={isUploading}
          isSaving={dialogState.saving}
        />
      ) : (
        <FormStep
          dialogProps={dialogCommonProps}
          dialogHeight={dialogHeight}
          previewUrl={fileUrl}
          previewLoading={isLoadingDownloadLink}
          displayPreview={displayPreview}
          fileExtension={currentFileExtension}
          filenameStandardization={filenameStandardization}
          shouldFieldsBeDisabled={shouldFieldsBeDisabled}
          control={control}
          issues={issues}
          isInvalidSds={isInvalidSds}
          hmbpWarningMessage={hmbpWarningMessage}
          isReadOnly={isReadOnly}
          onResetToUploadStep={resetToUploadStep}
          disableDocumentTypeSelection={disableDocumentTypeSelection}
          documentPickerOptions={documentPickerOptions}
          activitySelectItems={activitySelectItems}
          showTagPicker={showTagPicker}
          showFacilityPicker={showFacilityPicker}
          tenantId={tenantId}
          existingDefaults={existingDefaults}
          editingDocumentId={dialogState.documentId}
        />
      )}
    </FormProvider>
  );
}
